import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Plus, FileText, Search } from 'lucide-react';
import { toast } from 'sonner';
import { AddFeeDialog, EditFeeDialog, BulkEntryDialog } from './FeeStructureDialogs';
import { BulkActionsBar, FeeStructureTable } from './FeeStructureTable';

const defaultFeeForm = {
  project_id: '', stage: '', deliverable: '', percentage: '', amount: '',
  tentative_billing_date: '', deliverable_status: 'not_started',
  invoice_status: 'not_invoiced', payment_status: 'pending'
};

export const FeeStructureTab = ({ projects, feeStructure, formatCurrency, onRefresh }) => {
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [feeProjectSearch, setFeeProjectSearch] = useState('');
  const [feeSearchFocused, setFeeSearchFocused] = useState(false);
  const [selectedFeeItems, setSelectedFeeItems] = useState([]);
  const [isFeeDialogOpen, setIsFeeDialogOpen] = useState(false);
  const [isEditFeeDialog, setIsEditFeeDialog] = useState(false);
  const [isBulkEntryDialog, setIsBulkEntryDialog] = useState(false);
  const [editingFeeItem, setEditingFeeItem] = useState(null);
  const [feeForm, setFeeForm] = useState(defaultFeeForm);
  const [bulkEntryText, setBulkEntryText] = useState('');
  const [feeSortField, setFeeSortField] = useState('stage');
  const [feeSortOrder, setFeeSortOrder] = useState('asc');

  const filteredFeeStructure = useMemo(() => {
    if (!selectedProjectId) return [];
    const items = feeStructure.filter(item => item.project_id === selectedProjectId);
    return [...items].sort((a, b) => {
      const aVal = (a[feeSortField] || '').toString().toLowerCase();
      const bVal = (b[feeSortField] || '').toString().toLowerCase();
      return feeSortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
  }, [feeStructure, selectedProjectId, feeSortField, feeSortOrder]);

  const feeTotals = useMemo(() => ({
    percentage: filteredFeeStructure.reduce((sum, f) => sum + (f.percentage || 0), 0),
    amount: filteredFeeStructure.reduce((sum, f) => sum + (f.amount || 0), 0)
  }), [filteredFeeStructure]);

  const toggleFeeSort = (field) => {
    if (feeSortField === field) {
      setFeeSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setFeeSortField(field);
      setFeeSortOrder('asc');
    }
  };

  const getProjectValue = (projectId) => {
    const project = projects.find(p => p.project_id === projectId);
    return project?.budget || 0;
  };

  const resetFeeForm = () => {
    setFeeForm({ ...defaultFeeForm, project_id: selectedProjectId });
    setEditingFeeItem(null);
  };

  const handleFeeSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/fee-structure`, {
        ...feeForm, percentage: parseFloat(feeForm.percentage), amount: parseFloat(feeForm.amount)
      }, { withCredentials: true });
      toast.success('Fee structure item added');
      setIsFeeDialogOpen(false);
      resetFeeForm();
      onRefresh();
    } catch { toast.error('Failed to add fee structure item'); }
  };

  const handleEditFeeSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API_URL}/fee-structure/${editingFeeItem.item_id}`, {
        stage: feeForm.stage, deliverable: feeForm.deliverable,
        percentage: parseFloat(feeForm.percentage), amount: parseFloat(feeForm.amount),
        tentative_billing_date: feeForm.tentative_billing_date,
        deliverable_status: feeForm.deliverable_status,
        invoice_status: feeForm.invoice_status, payment_status: feeForm.payment_status
      }, { withCredentials: true });
      toast.success('Fee structure item updated');
      setIsEditFeeDialog(false);
      resetFeeForm();
      onRefresh();
    } catch { toast.error('Failed to update fee structure item'); }
  };

  const handleDeleteFeeItem = async (itemId) => {
    if (!window.confirm('Delete this fee structure item?')) return;
    try {
      await axios.delete(`${API_URL}/fee-structure/${itemId}`, { withCredentials: true });
      toast.success('Fee structure item deleted');
      onRefresh();
    } catch { toast.error('Failed to delete fee structure item'); }
  };

  const handleBulkStatusUpdate = async (field, value) => {
    if (selectedFeeItems.length === 0) { toast.error('No items selected'); return; }
    try {
      await Promise.all(selectedFeeItems.map(itemId =>
        axios.patch(`${API_URL}/fee-structure/${itemId}`, { [field]: value }, { withCredentials: true })
      ));
      toast.success(`Updated ${selectedFeeItems.length} items`);
      setSelectedFeeItems([]);
      onRefresh();
    } catch { toast.error('Failed to update items'); }
  };

  const handleBulkEntrySubmit = async () => {
    if (!selectedProjectId) { toast.error('Please select a project first'); return; }
    const lines = bulkEntryText.trim().split('\n').filter(line => line.trim());
    if (lines.length === 0) { toast.error('No entries to add'); return; }
    const projectValue = getProjectValue(selectedProjectId);
    const entries = [];
    for (const line of lines) {
      const parts = line.split('|').map(p => p.trim());
      if (parts.length >= 3) {
        const percentage = parseFloat(parts[2]) || 0;
        entries.push({
          project_id: selectedProjectId, stage: parts[0], deliverable: parts[1],
          percentage, amount: (percentage / 100) * projectValue,
          tentative_billing_date: parts[3] || null,
          deliverable_status: 'not_started', invoice_status: 'not_invoiced', payment_status: 'pending'
        });
      }
    }
    if (entries.length === 0) { toast.error('Invalid format. Use: Stage | Deliverable | Percentage | Date (optional)'); return; }
    try {
      await Promise.all(entries.map(entry => axios.post(`${API_URL}/fee-structure`, entry, { withCredentials: true })));
      toast.success(`Added ${entries.length} items`);
      setIsBulkEntryDialog(false);
      setBulkEntryText('');
      onRefresh();
    } catch { toast.error('Failed to add entries'); }
  };

  // Project search dropdown
  const filteredProjects = useMemo(() => {
    const term = feeProjectSearch.toLowerCase();
    return projects.filter(p =>
      !term || p.name.toLowerCase().includes(term) ||
      (p.project_code && p.project_code.toLowerCase().includes(term))
    );
  }, [projects, feeProjectSearch]);

  const selectedProject = projects.find(p => p.project_id === selectedProjectId);

  return (
    <Card className="p-6" data-testid="fee-structure-tab">
      {/* Header: search + action buttons */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-heading font-semibold flex items-center gap-2">
            <FileText className="h-5 w-5" /> Project Fee Structure
          </h3>
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={feeProjectSearch}
              onChange={(e) => {
                setFeeProjectSearch(e.target.value);
                setFeeSearchFocused(true);
                const term = e.target.value.toLowerCase();
                if (!term) { setSelectedProjectId(''); return; }
                const exactMatch = projects.find(p =>
                  p.project_code?.toLowerCase() === term || p.name.toLowerCase() === term
                );
                if (exactMatch) setSelectedProjectId(exactMatch.project_id);
                else setSelectedProjectId('');
              }}
              onFocus={() => setFeeSearchFocused(true)}
              onBlur={() => setTimeout(() => setFeeSearchFocused(false), 200)}
              placeholder="Search by project code or name..."
              className="pl-9"
              data-testid="fee-project-search"
            />
            {feeSearchFocused && !selectedProjectId && (
              <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-48 overflow-y-auto">
                {filteredProjects.length > 0 ? filteredProjects.map(p => (
                  <button
                    key={p.project_id}
                    className="w-full text-left px-3 py-2 hover:bg-muted text-sm"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setSelectedProjectId(p.project_id);
                      setFeeProjectSearch(p.project_code ? `${p.project_code} - ${p.name}` : p.name);
                      setSelectedFeeItems([]);
                      setFeeSearchFocused(false);
                    }}
                  >
                    {p.project_code && <span className="text-primary/70 mr-2">{p.project_code}</span>}
                    {p.name}
                  </button>
                )) : (
                  <p className="px-3 py-2 text-sm text-muted-foreground">No projects found</p>
                )}
              </div>
            )}
          </div>
          {selectedProjectId && (
            <Button variant="ghost" size="sm" onClick={() => { setSelectedProjectId(''); setFeeProjectSearch(''); setSelectedFeeItems([]); }}>Clear</Button>
          )}
        </div>
        {selectedProjectId && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setIsBulkEntryDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />Bulk Add
            </Button>
            <Button onClick={() => { setFeeForm({ ...feeForm, project_id: selectedProjectId }); setIsFeeDialogOpen(true); }}>
              <Plus className="mr-2 h-4 w-4" />Add Deliverable
            </Button>
          </div>
        )}
      </div>

      <BulkActionsBar
        selectedFeeItems={selectedFeeItems}
        setSelectedFeeItems={setSelectedFeeItems}
        onBulkStatusUpdate={handleBulkStatusUpdate}
      />

      {!selectedProjectId ? (
        <div className="text-center py-12 text-muted-foreground">
          <FileText className="h-16 w-16 mx-auto mb-4 opacity-50" />
          <p>Select a project to view and manage its fee structure</p>
        </div>
      ) : (
        <>
          {selectedProject && (
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg mb-4">
              <span className="text-sm text-primary/70 bg-primary/10 px-2 py-0.5 rounded">{selectedProject.project_code || 'No Code'}</span>
              <span className="font-medium">{selectedProject.name}</span>
              {selectedProject.client_name && <span className="text-sm text-muted-foreground">| {selectedProject.client_name}</span>}
              <span className="text-sm text-muted-foreground ml-auto">Project Value: {formatCurrency(getProjectValue(selectedProjectId))}</span>
            </div>
          )}
          {filteredFeeStructure.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p>No fee structure items for this project yet</p>
              <p className="text-sm">Click "Add Deliverable" to create one</p>
            </div>
          ) : (
            <FeeStructureTable
              filteredFeeStructure={filteredFeeStructure}
              selectedFeeItems={selectedFeeItems}
              setSelectedFeeItems={setSelectedFeeItems}
              feeSortField={feeSortField}
              toggleFeeSort={toggleFeeSort}
              feeTotals={feeTotals}
              formatCurrency={formatCurrency}
              onEdit={(item) => { setEditingFeeItem(item); setFeeForm({ ...item }); setIsEditFeeDialog(true); }}
              onDelete={handleDeleteFeeItem}
              onRefresh={onRefresh}
            />
          )}
        </>
      )}

      {/* Dialogs */}
      <AddFeeDialog
        open={isFeeDialogOpen} onOpenChange={setIsFeeDialogOpen}
        form={feeForm} setForm={setFeeForm} onSubmit={handleFeeSubmit}
        getProjectValue={getProjectValue} selectedProjectId={selectedProjectId}
      />
      <EditFeeDialog
        open={isEditFeeDialog} onOpenChange={setIsEditFeeDialog}
        form={feeForm} setForm={setFeeForm} onSubmit={handleEditFeeSubmit}
      />
      <BulkEntryDialog
        open={isBulkEntryDialog} onOpenChange={setIsBulkEntryDialog}
        text={bulkEntryText} setText={setBulkEntryText} onSubmit={handleBulkEntrySubmit}
      />
    </Card>
  );
};
